import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FeatureModal from '../../../../DesignSystem/FeatureModal/FeatureModal';
import NoticeModal from '../../../../DesignSystem/NoticeModal/NoticeModal';
import { ReactComponent as SuceessIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-success-variant.svg';
import { ReactComponent as AlertIcon } from '../../../../assets/icons/svg/colored-icons/ic-colored-alert.svg';
import SyncMyCardImage from '../../../../assets/png/sync-my-card.png';
import FullPageLoader from '../../../../components/FullPageLoader';
import { flattenServerErrorResponse } from '../../../../helpers/errors.helper';
import { IFlattenServerErrorResponse, IPostLinkData, IServerErrorResponse } from '../../../../types';
import { PlaidActionWrapper } from '../../../SendMoney/components/PlaidActionWrapper';
import { LinkTokenType, useLinkCustomertoPlaid } from '../../hooks/useLinkCustomertoPlaid';
import { LinkedAccount, useLinkedAccounts } from '../../hooks/useLinkedAccounts';
import { PlaidTokenResponse, usePlaidToken } from '../../hooks/usePlaidToken';
import { useStore } from '../../../../store';
import { TransactionTabs } from '../../state/createTransactionSlice';

interface SyncCardModalProps {
  showPlaidOnly?: boolean;
  expiredCard?: LinkedAccount | null;
}

const SuccessModalContent = () => {
  const { t } = useTranslation();
  return (
    <div className='text-center font-metricR text-[14px] mb-[48px] mt-[8px]'>
      <div className='mb-[12px]'>
        {t('TRANSACTIONS_FACELIFT.SYNC_CARD.SUCCESS_MODAL.TEXT')}
        <span className='font-metricRSemibold'>
          {t('TRANSACTIONS_FACELIFT.SYNC_CARD.SUCCESS_MODAL.CREDIT_CARD_PAYMENT')}
        </span>
        .
      </div>
      <div>
        {' '}
        {t('TRANSACTIONS_FACELIFT.SYNC_CARD.SUCCESS_MODAL.TEXT2')}
      </div>
    </div>
  );
};

const NoCardSelectedErrorModalContent = () => {
  const { t } = useTranslation();
  return (
    <div className='text-center font-metricR text-[14px] mb-[48px] mt-[8px]'>
      <div className='mb-[12px]'>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.NOT_A_CARD_ERROR_MODAL.TEXT')}</div>
      <div>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.NOT_A_CARD_ERROR_MODAL.TEXT2')}</div>
    </div>
  );
};

const PartialSuccessModalContent = () => {
  const { t } = useTranslation();
  return (
    <div className='text-center font-metricR text-[14px] mb-[23px] mt-[8px] flex justify-center flex-col items-center'>
      <div className='mb-[32px] w-[360px]'>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.PARTIAL_SUCCESS_MODAL.TEXT')}</div>
      <div className='bg-action00 border-action01 border-[1px] rounded-[8px] w-full py-[10px]'>
        {t('TRANSACTIONS_FACELIFT.SYNC_CARD.PARTIAL_SUCCESS_MODAL.HIGHLIGHTED')}
      </div>
    </div>
  );
};

const MaxLinkedAccountsErrorModalContent = () => {
  const { t } = useTranslation();
  return (
    <div className='text-center font-metricR text-[14px] mb-[48px] mt-[8px]'>
      <div className='mb-[12px]'>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.MAX_LINKED_ACCOUNTS_ERROR_MODAL.TEXT')}</div>
    </div>
  );
};

const DuplicateCardErrorModalContent = () => {
  const { t } = useTranslation();
  return (
    <div className='text-center font-metricR text-[14px] mb-[48px] mt-[8px]'>
      <div className='mb-[12px]'>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.DUPLICATE_CARD_ERROR_MODAL.TEXT')}</div>
    </div>
  );
};

enum LinkErrors {
  PLAID_NO_ACCOUNT = 4250,
  PLAID_TRANSACTION_REACHED_MAXIMUM_LINKED_ACCOUNTS_LIMIT = 50731,
  LINKED_ACCOUNT_ALREADY_EXISTS = 50732,
}

const SyncCardModal = (props: SyncCardModalProps) => {
  const { showPlaidOnly, expiredCard } = props;
  const { t } = useTranslation();
  const { mutateAsync: getPlaidToken, isLoading: isPlaidTokenLoading } = usePlaidToken();
  const [plaidToken, setPlaidToken] = useState<string>('');
  const [showSuccessModal, setShowSuccessModal] = useState<boolean>(false);
  const [showPlaidModal, setShowPlaidModal] = useState<boolean>(false);
  const [showNoCardErrorModal, setShowNoCardErrorModal] = useState<boolean>(false);
  const [showPartialSuccessModal, setShowPartialSuccessModal] = useState<boolean>(false);
  const [showMaxLinkedAccountsErrorModal, setShowMaxLinkedAccountsErrorModal] = useState<boolean>(false);
  const [showDuplicateCardErrorModal, setShowDuplicateCardErrorModal] = useState<boolean>(false);
  const { setSelectedTransactionTab } = useStore((state) => state);
  const { refetch: refetchCards, isLoading: isRefetchCardsLoading } = useLinkedAccounts();
  const setShowSyncCardModal = useStore((state) => state.setShowSyncCardModal);
  const showSyncCardModal = useStore((state) => state.showSyncCardModal);
  const setSelectedSyncedCard = useStore((state) => state.setSelectedSyncedCard);

  const handleLinkError = (error: IServerErrorResponse) => {
    setShowPlaidModal(false);
    setShowSuccessModal(false);
    const errors: IFlattenServerErrorResponse = flattenServerErrorResponse(error);
    if (errors.code === LinkErrors.PLAID_NO_ACCOUNT) {
      setShowNoCardErrorModal(true);
    } else if (errors.code === LinkErrors.PLAID_TRANSACTION_REACHED_MAXIMUM_LINKED_ACCOUNTS_LIMIT) {
      setShowMaxLinkedAccountsErrorModal(true);
    } else if (errors.code === LinkErrors.LINKED_ACCOUNT_ALREADY_EXISTS) {
      setShowDuplicateCardErrorModal(true);
    }
  };

  const { mutateAsync: linkPlaidToken, isLoading: isLinkPlaidTokenLoading } = useLinkCustomertoPlaid(
    LinkTokenType.TRANSACTIONS,
    handleLinkError,
  );

  const handlePlaidFlowStart = async () => {
    setPlaidToken('');
    const res: PlaidTokenResponse = await getPlaidToken(expiredCard ? expiredCard.institutionId : undefined);
    setPlaidToken(res?.linkToken?.link_token);
    setShowSyncCardModal(false);
    setShowPlaidModal(true);
  };

  const handlePostPlaidFlow = async (data: IPostLinkData) => {
    const res = await linkPlaidToken(data);
    setShowPlaidModal(false);
    if (res?.nonValidBankAccounts?.length > 0) {
      setShowPartialSuccessModal(true);
      return;
    }
    setShowSuccessModal(true);
  };

  const refreshAfterSuccess = async () => {
    const { data } = await refetchCards();
    if (data && data.length > 0) {
      setSelectedSyncedCard(data[0]);
    }
    setSelectedTransactionTab(TransactionTabs.LINKED_CREDIT_CARDS);
  };

  const handlePostPartialSuccessModal = async () => {
    setShowPartialSuccessModal(false);
    await refreshAfterSuccess();
  };

  const handlePostSuccessModal = async () => {
    setShowSuccessModal(false);
    await refreshAfterSuccess();
  };

  useEffect(() => {
    if (showPlaidOnly && !showSyncCardModal) {
      handlePlaidFlowStart();
    }
  }, [showSyncCardModal, showPlaidOnly]);

  if (isLinkPlaidTokenLoading || isRefetchCardsLoading) {
    return <FullPageLoader />;
  }

  return (
    <>
      {showSyncCardModal && !showPlaidOnly && (
        <FeatureModal
          onClickOutside={() => setShowSyncCardModal(false)}
          id='sync-card-modal'
          title={t('SYNC_CARD.SYNC_MODAL.TITLE')}
          content={(
            <>
              <p className='text-gray-700 mb-6 text-center'>{t('SYNC_CARD.SYNC_MODAL.TEXT')}</p>
              <p className='text-gray-700 text-center my-[24px] text-bw07'>{t('SYNC_CARD.SYNC_MODAL.TEXT2')}</p>
            </>
          )}
          disclaimer={(
            <>
              {t('SYNC_CARD.SYNC_MODAL.DISCLAIMER')}
              <a
                href='https://www.irs.gov/forms-pubs/guide-to-business-expense-resources#en_US_2020_publink1000208614'
                target='_blank'
                rel='noreferrer'
                className='underline'
              >
                {t('SYNC_CARD.SYNC_MODAL.IRS')}
              </a>
              {t('SYNC_CARD.SYNC_MODAL.DISCLAIMER2')}
            </>
          )}
          image={SyncMyCardImage}
          secondaryButtonText={t('SYNC_CARD.SYNC_MODAL.CANCEL')}
          buttonText={t('SYNC_CARD.SYNC_MODAL.CONTINUE')}
          onClick={handlePlaidFlowStart}
          isButtonLoading={isPlaidTokenLoading}
        />
      )}
      {plaidToken && showPlaidModal && (
        <PlaidActionWrapper
          shouldAutoOpenLink
          linkStyle=''
          linkTitle=''
          linkToken={plaidToken}
          isLoading={false}
          isFetchLinkToken={false}
          callbackHandler={() => { }}
          handlePostLink={handlePostPlaidFlow}
        />
      )}
      {showSuccessModal && (
        <NoticeModal
          title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.SUCCESS_MODAL.TITLE')}
          onClickOutside={handlePostSuccessModal}
          id='syncCardNoticeModal'
          image={<SuceessIcon />}
          buttonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.SUCCESS_MODAL.GOT_IT')}
          onSubmit={handlePostSuccessModal}
          content={<SuccessModalContent />}
        />
      )}
      {showNoCardErrorModal && (
        <NoticeModal
          title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.NOT_A_CARD_ERROR_MODAL.TITLE')}
          onClickOutside={() => setShowNoCardErrorModal(false)}
          id='syncCardErrorModal'
          image={<AlertIcon />}
          buttonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.NOT_A_CARD_ERROR_MODAL.GOT_IT')}
          onSubmit={() => setShowNoCardErrorModal(false)}
          content={<NoCardSelectedErrorModalContent />}
        />
      )}
      {showPartialSuccessModal && (
        <NoticeModal
          title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.PARTIAL_SUCCESS_MODAL.TITLE')}
          onClickOutside={handlePostPartialSuccessModal}
          id='syncCardPartialSuccessModal'
          image={<AlertIcon />}
          buttonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.PARTIAL_SUCCESS_MODAL.GOT_IT')}
          onSubmit={handlePostPartialSuccessModal}
          content={<PartialSuccessModalContent />}
        />
      )}
      {showMaxLinkedAccountsErrorModal && (
        <NoticeModal
          title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.MAX_LINKED_ACCOUNTS_ERROR_MODAL.TITLE')}
          onClickOutside={() => setShowMaxLinkedAccountsErrorModal(false)}
          id='syncCardMaxLinkedAccountsErrorModal'
          image={<AlertIcon />}
          buttonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.MAX_LINKED_ACCOUNTS_ERROR_MODAL.GOT_IT')}
          onSubmit={() => setShowMaxLinkedAccountsErrorModal(false)}
          content={<MaxLinkedAccountsErrorModalContent />}
        />
      )}
      {showDuplicateCardErrorModal && (
        <NoticeModal
          title={t('TRANSACTIONS_FACELIFT.SYNC_CARD.DUPLICATE_CARD_ERROR_MODAL.TITLE')}
          onClickOutside={() => setShowDuplicateCardErrorModal(false)}
          id='syncCardDuplicateCardErrorModal'
          image={<AlertIcon />}
          buttonText={t('TRANSACTIONS_FACELIFT.SYNC_CARD.DUPLICATE_CARD_ERROR_MODAL.GOT_IT')}
          onSubmit={() => setShowDuplicateCardErrorModal(false)}
          content={<DuplicateCardErrorModalContent />}
        />
      )}
    </>
  );
};
export default SyncCardModal;
